import { Controller } from "@hotwired/stimulus"

// A draft belongs to one card. Other cards and child-record editors can save
// independently without replacing it — not even a save that re-renders this card because
// it depends on the other one. A failed submission never clears the draft.
export default class extends Controller {
  static targets = ["panel", "edit", "editLabel", "form", "summary", "pending", "failure", "errors"]
  static values = {
    expanded: Boolean,
    saved: Boolean,
    editing: String,
    discard: String,
    failure: String,
  }

  connect() {
    this.dirty = this.expandedValue
    this.onLeave = (event) => {
      if (!this.dirty || this.saving || !this.element.isConnected) return
      if (event.type === "beforeunload") {
        event.preventDefault()
        event.returnValue = ""
      } else if (!window.confirm(this.discardValue)) {
        event.preventDefault()
      }
    }
    // While this card saves, its own stream answer must land; the draft is not pending then.
    this.keepDraft = (event) => {
      if (!this.dirty || this.saving || !this.element.isConnected) return
      const frame = this.element.closest("turbo-frame")
      if (frame && event.target.getAttribute("target") === frame.id) event.preventDefault()
    }
    window.addEventListener("beforeunload", this.onLeave)
    document.addEventListener("turbo:before-visit", this.onLeave)
    document.addEventListener("turbo:before-stream-render", this.keepDraft)
    if (this.hasErrorsTarget) this.errorsTarget.focus()
    this.updatePending()
    if (this.savedValue && this.hasEditTarget) this.editTarget.focus({ preventScroll: true })
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.onLeave)
    document.removeEventListener("turbo:before-visit", this.onLeave)
    document.removeEventListener("turbo:before-stream-render", this.keepDraft)
  }

  edit() {
    this.panelTarget.hidden = false
    this.editTarget.disabled = true
    this.editLabelTarget.textContent = this.editingValue
    this.editTarget.setAttribute("aria-expanded", "true")
    this.formTarget
      .querySelector('input:not([type="hidden"]), select, textarea, button')
      ?.focus({ preventScroll: true })
  }

  changed() {
    if (this.panelTarget.hidden) return
    this.dirty = true
    this.updatePending()
  }

  updatePending() {
    this.pendingTarget.hidden = !this.dirty
  }

  cancel(event) {
    if (this.dirty && !window.confirm(this.discardValue)) {
      event.preventDefault()
      return
    }
    // The frame GET restores all controls (including reordered export columns and
    // added signature rows) from the server. form.reset() would not restore these.
    this.dirty = false
  }

  submitting() {
    this.saving = true
    this.failureTarget.hidden = true
  }

  submitted(event) {
    this.saving = false
    if (event.detail.success) {
      this.dirty = false
    } else {
      this.dirty = true
      this.failureTarget.textContent = this.failureValue
      this.failureTarget.hidden = false
    }
    this.updatePending()
  }
}
